import React, { MutableRefObject } from 'react'
import { loadStripe } from '@stripe/stripe-js'
import {
  Elements,
  useStripe,
  useElements,
  CardNumberElement,
  CardExpiryElement,
  CardCvcElement,
} from '@stripe/react-stripe-js'
import {
  STRIPE_ELEMENT_OPTIONS,
  StripeCvcDateWrapper,
  StripeFieldWrapper,
  StripeFormStyled,
  ToasterWrapper,
} from 'Features/checkout/components/StripeForm/Stripe.styled'
import { Toast } from '@clickcease/cc-client-components-lib'
import { useTranslation } from 'react-i18next'

type CheckoutFormProps = {
  onSuccess: (token: string) => void
  onError: (token: string) => void
  submitRef: MutableRefObject<any>
  setIsLoading: (value: boolean) => void
  hasActiveSubscription?: boolean
  reactivation?: boolean
  isLoadingUpdateBillingDetails?: boolean
}

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_KEY || '')
const CheckoutForm = ({
  onSuccess,
  submitRef,
  setIsLoading,
  onError,
  hasActiveSubscription,
  reactivation,
  isLoadingUpdateBillingDetails,
}: CheckoutFormProps) => {
  const stripe = useStripe()
  const elements = useElements()
  const { t } = useTranslation('checkout')

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault()

    if (hasActiveSubscription) {
      onSuccess('')
      return
    }

    const cardElement = elements?.getElement(CardNumberElement)
    if (!stripe || !cardElement) return

    setIsLoading(true)
    try {
      const result = await stripe.createToken(cardElement)
      if (result.error) {
        onError(result.error.message || '')
        setIsLoading(false)
      } else {
        onSuccess(result.token?.id || '')
      }
    } catch (err) {
      console.error(err)
      onError('An unexpected error occurred')
      setIsLoading(false)
    }
  }

  return (
    <StripeFormStyled onSubmit={handleSubmit}>
      {!hasActiveSubscription ? (
        <>
          <StripeFieldWrapper>
            <CardNumberElement id="cardNumber" options={STRIPE_ELEMENT_OPTIONS} />
          </StripeFieldWrapper>
          <StripeCvcDateWrapper>
            <StripeFieldWrapper css={{ width: '45%' }}>
              <CardExpiryElement id="expiry" options={STRIPE_ELEMENT_OPTIONS} />
            </StripeFieldWrapper>
            <StripeFieldWrapper css={{ width: '45%' }}>
              <CardCvcElement id="cvc" options={STRIPE_ELEMENT_OPTIONS} />
            </StripeFieldWrapper>
          </StripeCvcDateWrapper>
        </>
      ) : (
        !isLoadingUpdateBillingDetails && (
          <ToasterWrapper reactivation={reactivation}>
            <Toast type="info">{t('sub_exists_vat_failed_error_msg')}</Toast>
          </ToasterWrapper>
        )
      )}
      <button ref={submitRef} type="submit" style={{ display: 'none' }} />
    </StripeFormStyled>
  )
}

export const StripeWrapper = ({
  onSuccess,
  onError,
  submitRef,
  setIsLoading,
  hasActiveSubscription,
  reactivation,
  isLoadingUpdateBillingDetails,
}: CheckoutFormProps) => (
  <Elements stripe={stripePromise}>
    <CheckoutForm
      onSuccess={onSuccess}
      onError={onError}
      submitRef={submitRef}
      setIsLoading={setIsLoading}
      hasActiveSubscription={hasActiveSubscription}
      reactivation={reactivation}
      isLoadingUpdateBillingDetails={isLoadingUpdateBillingDetails}
    />
  </Elements>
)
